import * as React from 'react';
import { forwardRef, ComponentPropsWithoutRef } from 'react';
import clsx from 'clsx';
import { LABEL_ID_SUFFIX } from '@constants/individualPortal/customGrouping/customGrouping.constants';

type GICheckboxPropsPicked =
  'id'
  | 'name'
  | 'onBlur'
  | 'onChange'
  | 'onFocus'
  | 'checked'
  | 'disabled'
  | 'required'
  | 'value'

export interface IGICheckboxProps extends Pick<ComponentPropsWithoutRef<'input'>, GICheckboxPropsPicked> {
  /** ID of the element that labels this checkbox (for aria-labelledby). Defaults to `${id}-label` */
  labelId?: string;
  /** ID of additional element to prepend to aria-labelledby (e.g., group label like "Shop for these members") */
  groupLabelId?: string;
}

export const GICheckbox = forwardRef<HTMLInputElement, IGICheckboxProps>(function GICheckbox(
    {
        id,
        name,
        required = false,
        checked,
        value,
        onChange,
        disabled = false,
        labelId,
        groupLabelId,
    },
    ref
) {
    const inputClassNames = clsx('usa-checkbox__input gi-checkbox__input', `required-${required}`);
    const checkboxLabelId = labelId || `${id}${LABEL_ID_SUFFIX}`;
    // If groupLabelId provided, prepend it so screen reader announces: "Group label, Member name, checkbox, state"
    const ariaLabelledBy = groupLabelId ? `${groupLabelId} ${checkboxLabelId}` : checkboxLabelId;
    return (
        <input
            id={id}
            name={name}
            aria-labelledby={ariaLabelledBy}
            checked={checked}
            className={inputClassNames}
            disabled={disabled}
            data-automation-id={id}
            data-testid={id}
            onChange={onChange}
            ref={ref}
            required={required}
            type='checkbox'
            value={value}
        />
    );
});
